// @spec AI-STR-033
'use client';


import { useUser } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { DollarSign, Download, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  CostByFeatureChart,
  CostByOrgTable,
  CostForecastCard,
  CostLimitsForm,
  CostOverviewCards,
  CostTimelineChart,
  useExportPlatformCosts,
  usePlatformCostByFeature,
  usePlatformCostForecast,
  usePlatformCostLimits,
  usePlatformCostSummary,
  usePlatformCostTimeline,
  useUpdatePlatformCostLimits,
} from '@/features/admin';
import type { UsagePeriod } from '@/features/admin';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/admin/ai-costs')({
  component: AICostsPage,
});

function isSuperAdmin(user: ReturnType<typeof useUser>['user']): boolean {
  if (!user) return false;
  const publicMetadata = user.publicMetadata as Record<string, unknown> | undefined;
  return publicMetadata?.role === 'super_admin';
}

type Tab = 'overview' | 'organizations' | 'settings';

const PERIOD_OPTIONS: { value: UsagePeriod; label: string }[] = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
];

function AICostsPage() {
  const { user, isLoaded: userLoaded } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [period, setPeriod] = useState<UsagePeriod>('month');

  const hasAccess = isSuperAdmin(user);

  const summaryQuery = usePlatformCostSummary(period);
  const timelineQuery = usePlatformCostTimeline(period);
  const featureQuery = usePlatformCostByFeature(period);
  const forecastQuery = usePlatformCostForecast();
  const limitsQuery = usePlatformCostLimits();
  const updateLimitsMutation = useUpdatePlatformCostLimits();
  const exportMutation = useExportPlatformCosts();

  // Loading state
  if (!userLoaded) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-6">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-muted" />
              <div className="space-y-2">
                <div className="h-6 w-48 rounded bg-muted" />
                <div className="h-4 w-32 rounded bg-muted" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-32 rounded-xl bg-muted" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Access denied
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-lg text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-destructive/10 p-4">
              <ShieldAlert className="h-12 w-12 text-destructive" />
            </div>
          </div>
          <h1 className="mb-2 text-2xl font-semibold text-foreground">{t('accessDenied.title')}</h1>
          <p className="mb-6 text-muted-foreground">
            {t('accessDenied.aiCostsDenied')}
          </p>
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => {
              void navigate({ to: '/dashboard' });
            }}
          >
            {t('accessDenied.returnToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <DollarSign className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">AI Costs</h1>
              <p className="text-sm text-muted-foreground">
                Platform-wide AI cost monitoring and management
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Period selector */}
            <select
              value={period}
              onChange={(e) => { setPeriod(e.target.value as UsagePeriod); }}
              className="rounded-md border bg-background px-3 py-2 text-sm"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {/* Export button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => { exportMutation.mutate({ period }); }}
              disabled={exportMutation.isPending}
            >
              <Download className="mr-2 h-4 w-4" />
              {exportMutation.isPending ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-1 border-b">
          {(['overview', 'organizations', 'settings'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              className={cn(
                'border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
              onClick={() => { setActiveTab(tab); }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <CostOverviewCards data={summaryQuery.data} isLoading={summaryQuery.isLoading} />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <CostTimelineChart data={timelineQuery.data} isLoading={timelineQuery.isLoading} />
              <CostForecastCard data={forecastQuery.data} isLoading={forecastQuery.isLoading} />
            </div>

            <CostByFeatureChart
              data={featureQuery.data?.features ?? []}
              isLoading={featureQuery.isLoading}
            />
          </div>
        )}

        {activeTab === 'organizations' && <CostByOrgTable period={period} />}

        {activeTab === 'settings' && (
          <CostLimitsForm
            limits={limitsQuery.data}
            isLoading={limitsQuery.isLoading}
            onSave={(update) => { updateLimitsMutation.mutate(update); }}
            isSaving={updateLimitsMutation.isPending}
          />
        )}
      </div>
    </div>
  );
}
